// Partner-Portal-Pyramid (2026-06-24) — forward-looking payout summary
// card for the partner's own portal. Pairs realised YTD earnings with
// projected pipeline earnings (what they'll collect if active deals
// settle as currently shaped). Reads the new fields added to
// /partner/dashboard in this PR:
//   totals.ytd_partner_commission_earned        (already existed)
//   totals.pipeline_partner_commission_projection (NEW)
//   totals.active_deal_count_all
//   totals.pipeline_total_all
//
// Always renders — even with $0 across the board — so a brand-new
// partner sees the empty-state shape rather than a missing widget.
import React from "react";
import { TrendingUp, DollarSign, Briefcase, Layers } from "lucide-react";

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  });
}

export default function PipelineProjectionCard({ totals }) {
  const t = totals || {};
  const ytd = t.ytd_partner_commission_earned || 0;
  const pipeline = t.pipeline_partner_commission_projection || 0;
  const dealCount = t.active_deal_count_all || 0;
  const pipelineTotal = t.pipeline_total_all || 0;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-semibold text-[#021d49]">Your Payouts</h2>
        <p className="text-xs text-gray-500">Realised YTD + projected pipeline</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Realised YTD */}
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <DollarSign className="w-3.5 h-3.5 text-emerald-700" />
            <p className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700">YTD earned</p>
          </div>
          <p className="text-lg font-semibold text-[#021d49]">{fmtMoney(ytd)}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">From settled deals this year</p>
        </div>

        {/* Pipeline projection */}
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-amber-700" />
            <p className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">Pipeline projection</p>
          </div>
          <p className="text-lg font-semibold text-[#021d49]">{fmtMoney(pipeline)}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">If active deals settle as is</p>
        </div>

        {/* Active deals */}
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Briefcase className="w-3.5 h-3.5 text-gray-700" />
            <p className="text-[10px] uppercase tracking-wide font-semibold text-gray-700">Active deals</p>
          </div>
          <p className="text-lg font-semibold text-[#021d49]">{dealCount}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">In flight across your downline</p>
        </div>

        {/* Pipeline value */}
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Layers className="w-3.5 h-3.5 text-blue-700" />
            <p className="text-[10px] uppercase tracking-wide font-semibold text-blue-700">Pipeline value</p>
          </div>
          <p className="text-lg font-semibold text-[#021d49]">{fmtMoney(pipelineTotal)}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">Net loan amount in active deals</p>
        </div>
      </div>

      <p className="text-[10px] text-gray-400 mt-3 italic">
        Projections are estimates based on current commission splits and net loan amounts. Final payouts may
        differ once deals settle and any per-deal overrides are applied.
      </p>
    </div>
  );
}
